import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook for smart auto-refresh with visibility detection
 */
export const useAutoRefresh = (fetchFn, options = {}) => {
  const {
    interval = 30000, // 30 seconds
    enabled = true,
    onNewData = null
  } = options;

  const [hasNewData, setHasNewData] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const previousCountRef = useRef(null);

  const refresh = useCallback(async (silent = true) => {
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      const result = await fetchFn();

      // Check if count changed (simple change detection)
      if (!silent && previousCountRef.current !== null) {
        const currentCount = result?.length || result?.totalTickets || 0;
        if (currentCount !== previousCountRef.current) {
          setHasNewData(true);
          onNewData?.(result);
        }
      }

      previousCountRef.current = result?.length || result?.totalTickets || 0;
      setLastUpdateTime(Date.now());

      return result;
    } catch (error) {
      console.error('Auto-refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchFn, onNewData, isRefreshing]);

  const acceptNewData = useCallback(() => {
    setHasNewData(false);
    refresh(true);
  }, [refresh]);

  const dismissNewData = useCallback(() => {
    setHasNewData(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    // Set up polling
    intervalRef.current = setInterval(() => {
      // Only poll if page is visible
      if (!document.hidden) {
        refresh(false);
      }
    }, interval);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, do immediate refresh
        refresh(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, interval, refresh]);

  return {
    hasNewData,
    lastUpdateTime,
    isRefreshing,
    refresh: () => refresh(true),
    acceptNewData,
    dismissNewData
  };
};

export default useAutoRefresh;
